import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Button from 'tio-ui/components/button';
import Modal from 'tio-ui/components/modal';
import { VStack } from 'tio-ui/components/layout';
import { PhWarning } from 'ember-phosphor-icons';
import { updateReportRequest } from '../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type Owner from '@ember/owner';

export type PublishReportAction = 'publish' | 'unpublish';

interface PublishReportDialogSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (report: Report) => void;
    report: Report;
    action?: PublishReportAction;
    currentCompanyId?: string | null;
    companyName?: string;
  };
}

export default class PublishReportDialog extends Component<PublishReportDialogSignature> {
  @service declare store: StoreService;

  @tracked isPublishing = false;
  @tracked hasAcknowledgedRisk = false;

  constructor(owner: Owner, args: PublishReportDialogSignature['Args']) {
    super(owner, args);
  }

  get companyId() {
    return this.args.currentCompanyId || this.args.report?.metadata?.publishedCompanyId || null;
  }

  get visibilityLevel() {
    return this.companyId ? 'partner' : 'global';
  }

  get partnerAccessConfig() {
    return this.companyId ? [Number(this.companyId)] : [];
  }

  get action() {
    return this.args.action || 'publish';
  }

  get isUnpublish() {
    return this.action === 'unpublish';
  }

  get actionLabel() {
    return this.isUnpublish ? 'Unpublish' : 'Publish';
  }

  get runningLabel() {
    return this.isUnpublish ? 'Unpublishing...' : 'Publishing...';
  }

  get headerText() {
    return this.isUnpublish ? 'Unpublish Report?' : 'Publish Report?';
  }

  get acknowledgementText() {
    return this.isUnpublish
      ? 'I understand this is a sensitive action and have verified the report should be unpublished.'
      : 'I understand this is a sensitive action and have verified the report is safe to publish.';
  }

  handleConfirm = async () => {
    const report = this.args.report;
    if (!report?.id || !this.hasAcknowledgedRisk) return;
    // Final safety boundary: private reports are not publishable, regardless
    // of which caller opened this dialog (unpublish stays allowed so an
    // anomalous private+published report can be repaired).
    if (!this.isUnpublish && report.isPrivate) {
      console.error('Private reports are not publishable:', report.id);
      this.args.onClose();
      return;
    }

    this.isPublishing = true;
    try {
      const metadata = { ...(report.metadata || {}) };
      let visibilityLevel: string;
      let partnerAccessConfig: number[];

      if (this.isUnpublish) {
        metadata.scope = 'admin_only';
        delete metadata.publishedAt;
        delete metadata.publishedCompanyId;
        visibilityLevel = report.visibilityLevel || 'private';
        partnerAccessConfig = report.partnerAccessConfig || [];
      } else {
        metadata.scope = 'partner_admin';
        metadata.publishedAt = new Date().toISOString();
        metadata.publishedCompanyId = this.companyId || undefined;
        visibilityLevel = this.visibilityLevel;
        partnerAccessConfig = this.partnerAccessConfig;
      }

      const request = updateReportRequest(report.id, {
        visibilityLevel,
        partnerAccessConfig,
        metadata,
      });

      request.cacheOptions = { [SkipCache]: true };
      await this.store.request(request);
      // Write back only known camelCase schema fields (the PATCH response
      // echoes what we sent). Assigning the raw response attributes hits the
      // SchemaRecord set trap with dasherized keys ('report-type', …), which
      // throws before anything lands and left rows stale until reload.
      report.visibilityLevel = visibilityLevel;
      report.partnerAccessConfig = partnerAccessConfig;
      report.metadata = metadata;
      this.args.onPublish(report);
      this.args.onClose();
      this.hasAcknowledgedRisk = false;
    } catch (error) {
      console.error(`Failed to ${this.action} report:`, error);
    } finally {
      this.isPublishing = false;
    }
  };

  handleCancel = () => {
    this.hasAcknowledgedRisk = false;
    this.args.onClose();
  };

  handleAcknowledgementChange = (event: Event) => {
    this.hasAcknowledgedRisk = (event.target as HTMLInputElement).checked;
  };

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleCancel}} @size="md" data-test-publish-dialog>
      <:header>
        <div class="tio-row tio-fg-warning-body">
          <span class="tio-row-center h-9 w-9 rounded-full tio-bg-warning-soft tio-fg-warning">
            <PhWarning @size={{20}} />
          </span>
          <span class="tio-text-base-semibold tio-fg-primary">{{this.headerText}}</span>
        </div>
      </:header>

      <:body>
        <VStack class="tio-col-loose">
          <div class="space-y-2 text-sm tio-fg-secondary">
            <p>
              {{#if this.isUnpublish}}
                You are about to remove partner admin access to this report. Users who rely on this
                report may lose access immediately.
              {{else}}
                You are about to publish this report to partner admins. Published reports can expose
                configured report data outside the admin-only workspace.
              {{/if}}
            </p>
            <p class="font-semibold tio-fg-primary">
              {{#if this.isUnpublish}}
                Only unpublish after verifying this report should no longer be available to partner
                admins.
              {{else}}
                Only publish after verifying the report columns, filters, visibility, and company
                access are correct.
              {{/if}}
            </p>
          </div>

          <div class="rounded-md border tio-border-warning tio-bg-warning-soft p-3 text-sm">
            <p class="font-semibold tio-fg-warning-h">
              {{if this.isUnpublish "Unpublishing risk" "Publishing risk"}}
            </p>
            <ul class="mt-2 list-inside list-disc space-y-1 tio-fg-warning-body">
              {{#if this.isUnpublish}}
                <li>Partner admins may immediately lose access to this report.</li>
                <li>Dashboards, documentation, or workflows that reference the report may break.</li>
                <li>Users may lose visibility into expected operational reporting.</li>
              {{else}}
                <li>Partner admins may immediately access this report.</li>
                <li>Incorrect visibility can expose data to the wrong audience.</li>
                <li>Incorrect filters or columns can publish sensitive or misleading report results.</li>
              {{/if}}
            </ul>
          </div>

          <div class="rounded-md border tio-border-default tio-bg-sunken p-3 text-sm">
            <p class="font-medium tio-fg-primary">What will happen</p>
            <ul class="mt-1 list-inside list-disc tio-fg-secondary">
              <li>
                <span class="font-medium">Scope:</span>
                {{#if this.isUnpublish}}
                  Admin Only — this report will no longer be visible to partner admins
                {{else}}
                  Partner Admin — this report will be visible outside of admin
                {{/if}}
              </li>
              {{#unless this.isUnpublish}}
                <li>
                  <span class="font-medium">Visibility:</span>
                  {{#if this.companyId}}
                    Custom Partner Report — only
                    <strong class="tio-fg-primary">{{if
                        @companyName
                        @companyName
                        "the selected company"
                      }}</strong>
                    can see this report
                  {{else}}
                    Global — all partner admins can see this report
                  {{/if}}
                </li>
              {{/unless}}
            </ul>
          </div>

          <label
            class="tio-row-top-tight rounded-md border tio-border-default tio-bg-surface p-3 text-sm tio-fg-secondary"
          >
            <input
              type="checkbox"
              checked={{this.hasAcknowledgedRisk}}
              class="mt-0.5 h-4 w-4 rounded tio-border-default tio-fg-warning focus:tio-ring-warning"
              {{on "change" this.handleAcknowledgementChange}}
            />
            <span>
              {{this.acknowledgementText}}
            </span>
          </label>
        </VStack>
      </:body>

      <:footer>
        <div class="tio-flex-end gap-x-3">
          <Button @appearance="outlined" {{on "click" this.handleCancel}} data-test-publish-cancel>
            Cancel
          </Button>
          <Button
            @intent="danger"
            @isRunning={{this.isPublishing}}
            @isDisabled={{if this.hasAcknowledgedRisk false true}}
            {{on "click" this.handleConfirm}}
            data-test-publish-submit
          >
            {{if this.isPublishing this.runningLabel this.actionLabel}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
